import throttle from 'lodash.throttle';
import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';

// This creates a circular dependency
// import field from '#element/field/field';


const is_expanded_attr = 'aria-expanded';
const is_busy_attr = 'aria-busy';
const inert_attr = 'inert';
const body_$ = 'body';
const hover_attr = 'data-expandable-hover';
const hover_delay_attr = 'data-expandable-hover-delay';
const hover_active_attr = 'data-expandable-hover-active';
const disable_collapsed_attr = 'data-expandable-disable-collapsed';
const prevent_scroll_attr = 'data-expandable-prevent-scroll';
const autofocus_attr = 'data-expandable-autofocus';
const escape_close_attr = 'data-expandable-escape-close';
const outside_close_attr = 'data-expandable-outside-close';
const resize_close_attr = 'data-expandable-resize-close';
const exclusive_close_attr = 'data-expandable-exclusive-close';

let has_expandable = false;
let $body = null;

const hover_delay = {
	on: 200,
	off: 400,
};
const hover_timeouts = {
	on: [],
	off: [],
};


const module = {
	container_$: '[data-expandable]',
	content_$: '[data-expandable-content]',
	button_$: '[data-expandable-btn]',
	close_$: '[data-expandable-close]',
	target_attr: 'aria-controls',
	active_attr: 'data-is-active',
	toggle_start_e: 'expandable-toggle-start',
	toggle_end_e: 'expandable-toggle-end',
	
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('expandable')) return false;
		
		load.content(() => {
			has_expandable = assign_elements();
			if (!has_expandable) return false;
		});
		load.all(() => {
			has_expandable = assign_elements();
			if (!has_expandable) return false;
			
			window.addEventListener(g.resize_e, throttle(() => {
				close_on_resize();
			}, 150));
		});
	},
	
	
	/**
	 * Toggle expandable by ID
	 * @param {string} id
	 * @param {boolean} force
	 * @param {boolean} from_other
	 * @param {array} done_f
	 */
	toggle({id, force, from_other, done_f} = {}) {
		toggle_by_id(id, force, from_other, done_f);
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_expandable) return true;
	
	$body = document.querySelector(body_$);
	
	// Open when mouse enters button with hover
	document.addEventListener(g.mouse_over_e, e => {
		const $button = e.target.closest(module.button_$);
		if ($button && $button.getAttribute(hover_attr) === g.true_s) {
			start_hover($button, true);
		}
	});
	
	// Start to close when mouse leaves button with hover
	document.addEventListener(g.mouse_out_e, e => {
		const $button = e.target.closest(module.button_$);
		if ($button && $button.getAttribute(hover_attr) === g.true_s) {
			start_hover($button, false);
		}
	});
	
	// Toggle by click
	document.addEventListener(g.click_e, e => {
		const $button = e.target.closest(module.button_$);
		if ($button) {
			if ($button.getAttribute(hover_active_attr) === g.true_s) return false;
			
			e.preventDefault();
			toggle_by_button($button);
		}
		
		const $close = e.target.closest(module.close_$);
		if ($close) {
			e.preventDefault();
			const $target = $close.closest(module.container_$);
			toggle_by_target($target, false);
		}
	});
	
	// Start timer when mouse enters content area with hover
	document.addEventListener(g.mouse_over_e, e => {
		const $content = e.target.closest(module.content_$);
		if ($content) {
			const $target = $content.closest(module.container_$);
			const $buttons = find_buttons($target);
			if (!$buttons.length) return false;
			
			const $button = $buttons[0];
			if ($button.getAttribute(hover_attr) === g.true_s) {
				clearTimeout(hover_timeouts['off'][$target.id]);
			}
		}
	});
	
	// Start to close when mouse leaves content area with hover
	document.addEventListener(g.mouse_out_e, e => {
		const $content = e.target.closest(module.content_$);
		if ($content) {
			const $target = $content.closest(module.container_$);
			const $buttons = find_buttons($target);
			if (!$buttons.length) return false;
			
			const $button = $buttons[0];
			if ($button.getAttribute(hover_attr) === g.true_s) {
				start_hover($button, false);
			}
		}
	});
	
	// Close when clicking outside of expandable
	document.addEventListener(g.click_e, e => {
		
		// If any datepicker is visible, allow the click to pass through but don't
		// close any expandables, unless explicitly clicking on a close button. It
		// doesn't matter if the datepicker is an ancestor of the clicked element,
		// because sometimes we arrive here after the datepicker has already been
		// dismissed and removed from the DOM, so it's impossible to determine if
		// the click was inside of a datepicker.
		if (document.querySelector(g.datepicker_$)) return;
		// e.target.closest(field.datepicker_$) // Circular dependency
		
		document.querySelectorAll(module.container_$).forEach($target => {
			if ($target.getAttribute(outside_close_attr) === g.false_s) return false;
			
			const is_expanded = $target.getAttribute(module.active_attr);
			if (is_expanded === g.false_s) return false;
			
			// If $target is an ancestor of the clicked element, do not close the
			// expandable (at least, not here)
			if ($target.contains(e.target)) return true;
			
			// If the clicked element is not a button, close the expandable
			if (!e.target.closest(module.button_$)) {
				toggle_by_target($target, false, true);
				return false;
			}
			
			const $this_buttons = find_buttons($target);
			if (!$this_buttons.length) return false;
			
			// Close all other expandables, but only those whose $button is not an
			// ancestor of the clicked element (because closing by button is handled
			// elsewhere)
			$this_buttons.forEach($button => {
				if ($button.contains(e.target)) return true;
				
				toggle_by_button($button, false, true);
			});
		});
	});
	
	// Close when pressing Escape
	document.addEventListener(g.keydown_e, e => {
		if (e.key !== 'Escape') return;
		
		document.querySelectorAll(module.container_$).forEach($target => {
			if ($target.getAttribute(escape_close_attr) === g.false_s) return false;
			
			const is_expanded = $target.getAttribute(module.active_attr);
			if (is_expanded === g.false_s) return false;
			
			toggle_by_target($target, false);
		});
	});
	
	return true;
}


/**
 * Toggle by ID
 * @param {string} id
 * @param {boolean} force
 * @param {boolean} from_other
 * @param {array} done_f
 */
function toggle_by_id(id, force, from_other, done_f) {
	const $target = document.querySelector(`#${id}`);
	toggle_by_target($target, force, from_other, done_f);
}


/**
 * Toggle by button
 * @param {element} $button
 * @param {boolean} force
 * @param {boolean} from_other
 */
function toggle_by_button($button, force, from_other) {
	const $target = find_target($button);
	toggle_by_target($target, force, from_other);
}


/**
 * Toggle by target
 * @param {element} $target
 * @param {boolean} force: true = open, false = close
 * @param {boolean} from_other
 * @param {array} done_f: callbacks to run after transition
 */
function toggle_by_target($target, force = null, from_other = false, done_f = []) {
	if (!$target) {
		layout.allowScroll(true); // TODO: do we really want to do this?
		return false;
	}
	
	const is_expanded = $target.getAttribute(module.active_attr);
	if (force === true && is_expanded === g.true_s) return false;
	if (force === false && is_expanded === g.false_s) return false;
	
	const will_now_activate = (is_expanded === g.false_s);
	const is_active_s = (will_now_activate)
		? g.true_s
		: g.false_s;
	
	// Close other expandables
	if (will_now_activate && $target.hasAttribute(exclusive_close_attr)) {
		const $all_containers = document.querySelectorAll(module.container_$);
		
		// Close all others if this target has no exclusive keys
		const value = $target.getAttribute(exclusive_close_attr);
		if (!value) {
			$all_containers.forEach($this_target => {
				if ($this_target === $target) return false;
				
				toggle_by_target($this_target, false, true);
			});
		}
		
		// Close others with shared keys
		else {
			const exclusive_keys = get_exclusive_keys($target);
			
			$all_containers.forEach($this_target => {
				if ($this_target === $target) return false;
				
				const this_has_attr = $this_target.hasAttribute(exclusive_close_attr);
				const this_keys = get_exclusive_keys($this_target);
				const shared_keys = exclusive_keys.filter(x => this_keys.includes(x));
				
				if (
					!exclusive_keys.length
					|| shared_keys.length
					|| (this_has_attr && !this_keys.length)
				) {
					toggle_by_target($this_target, false, true);
				}
			});
		}
	}
	
	const $this_buttons = find_buttons($target);
	
	// Update button status, and blur if closed from another expandable
	$this_buttons.forEach($button => {
		$button.setAttribute(is_expanded_attr, is_active_s);
		if (from_other) {
			$button.blur();
		}
	});
	
	// Fire layout change before failing any checks
	document.dispatchEvent(new CustomEvent(layout.change_e));
	
	// Update scrolling
	if ($target.getAttribute(prevent_scroll_attr) === g.true_s) {
		layout.allowScroll(!will_now_activate);
	}
	
	const transition_done_callbacks = done_f || [];
	
	// Expand or collapse target
	$target.setAttribute(module.active_attr, is_active_s);
	$target.setAttribute(is_busy_attr, g.true_s);
	transition_done_callbacks.push(() => {
		if ($target.getAttribute(disable_collapsed_attr) === g.true_s) {
			$target.toggleAttribute(inert_attr, !will_now_activate);
		}
		$target.setAttribute(is_busy_attr, g.false_s);
		$target.dispatchEvent(new CustomEvent(module.toggle_end_e), {
			bubbles: true,
		});
		document.dispatchEvent(new CustomEvent(layout.change_e));
	});
	
	$target.addEventListener(g.transition_end_e, function done(e) {
		if (e.target !== $target) return false;
		
		$target.removeEventListener(g.transition_end_e, done);
		transition_done_callbacks.forEach(callback => callback());
		transition_done_callbacks.length = 0;
	});
	
	// Fallback in case transition end event doesn't fire
	setTimeout(() => {
		if (transition_done_callbacks.length) {
			transition_done_callbacks.forEach(callback => callback());
		}
	}, 500);
	
	// Fire toggle event
	$target.dispatchEvent(new CustomEvent(module.toggle_start_e, {
		bubbles: true,
		detail: {
			is_active: will_now_activate,
			force: force,
		},
	}));
	
	// If another expandable closed this one, stop
	if (from_other) return false;
	
	// Focus field in expanded target
	const $autofocus = $target.querySelector(`[${autofocus_attr}]`);
	if (will_now_activate && $autofocus) {
		transition_done_callbacks.push(() => {
			$autofocus.focus({
				preventScroll: true,
			});
		});
	}
}


/**
 * Find target
 * @param {element} $button
 * @return {element}
 */
function find_target($button) {
	const target_id = $button.getAttribute(module.target_attr);
	return document.querySelector(`#${target_id}`);
}


/**
 * Find buttons
 * @param {element} $target
 * @return {array}
 */
function find_buttons($target) {
	return document.querySelectorAll(`[${module.target_attr}="${$target.id}"]`);
}


/**
 * Close expandables on window resize
 */
function close_on_resize() {
	document.querySelectorAll(module.container_$).forEach($target => {
		if ($target.getAttribute(resize_close_attr) === g.false_s) return false;
		
		// TODO: if there is an autofocus element that will cause the keyboard to appear on mobile, then this needs to be delayed
		
		const $this_buttons = find_buttons($target);
		if (!$this_buttons.length) return false;
		
		$this_buttons.forEach($button => {
			toggle_by_button($button, false);
		});
	});
}


/**
 * Get button exclusive keys
 * @param {element} $target
 * @return {array}
 */
function get_exclusive_keys($target) {
	const exclusive_keys = $target.getAttribute(exclusive_close_attr) || '';
	return exclusive_keys.split(' ').filter(x => x);
}


/**
 * Start hover
 * @param {element} $button
 * @param {boolean} on
 */
function start_hover($button, on) {
	const direction = on ? 'on' : 'off';
	const opposite_direction = on ? 'off' : 'on';
	const target_id = $button.getAttribute(module.target_attr);
	const delay = ($button.getAttribute(hover_delay_attr) === g.true_s)
		? hover_delay[direction]
		: 0;
	
	clearTimeout(hover_timeouts[opposite_direction][target_id]);
	hover_timeouts[direction][target_id] = setTimeout(() => {
		$button.setAttribute(hover_active_attr, on ? g.true_s : g.false_s);
		toggle_by_button($button, on);
	}, delay);
}
